import type {
  PermissionAction,
  PermissionGroupValues,
  ProfilePermissions,
} from '@strength/domain';
import {
  isPermissionGranted,
  permissionActions,
  PERMISSION_GROUPS,
} from '@strength/domain';

/**
 * Resolves a (possibly sparse) stored document into a fully-resolved one:
 * every action of every group gets an explicit boolean via
 * `isPermissionGranted` (absent/true -> true, only explicit `false` -> false).
 *
 * Each action is resolved from its own stored value only — dependency gating
 * is left to the drawer's `dependencyDeniedBySubAction` UI state and to server
 * enforcement, so loading a document never forces a gated child false and
 * risks persisting a denial the administrator never chose.
 *
 * The drawer edits a document in this shape directly as its form state, so
 * group ids double as React Hook Form field paths (`library.tags` nests as
 * `library` -> `tags`, exactly as it's stored) and the edited value is what
 * `savePermissions` expects.
 */
export function resolvePermissionsDocument(
  permissions: ProfilePermissions,
): ProfilePermissions {
  const doc: Record<string, unknown> = {};
  for (const group of PERMISSION_GROUPS) {
    // Walk the group id's dot path (e.g. `library.exercises`) to extract
    // the group's raw (sparse) action map; absent key means granted.
    let stored: unknown = permissions;
    for (const segment of group.id.split('.')) {
      if (!stored || typeof stored !== 'object') {
        stored = {};
        break;
      }
      stored = (stored as Record<string, unknown>)[segment];
    }
    const values = (
      stored && typeof stored === 'object' ? stored : {}
    ) as PermissionGroupValues;

    // Resolve each leaf through isPermissionGranted (absent/true -> true).
    const resolved: PermissionGroupValues = {};
    for (const action of permissionActions(group.id) as PermissionAction[]) {
      resolved[action] = isPermissionGranted(values[action]);
    }

    // Write the resolved group back at its dot path.
    const segments = group.id.split('.');
    let node: Record<string, unknown> = doc;
    for (const segment of segments.slice(0, -1)) {
      node[segment] = (node[segment] as Record<string, unknown>) ?? {};
      node = node[segment] as Record<string, unknown>;
    }
    const last = segments[segments.length - 1]!;
    node[last] = resolved;
  }
  return doc as ProfilePermissions;
}
